import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, User, MapPin, Package, Upload, CheckCircle2, FileText, DollarSign, Image as ImageIcon } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function ProductSourcingScreen() {
  const {
    navigate
  } = useNavigation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    description: '',
    category: '',
    quantity: '',
    budget: '',
    notes: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSimulateUpload = () => {
    if (hasUploaded) return;
    setIsUploading(true);
    setTimeout(() => {
      setIsUploading(false);
      setHasUploaded(true);
    }, 1500);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, validate and send data to API here
    setIsSubmitted(true);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-8" data-id="element-1142">
      <Header title="Product Sourcing" data-id="element-1143" />

      <AnimatePresence mode="wait" data-id="element-1144">
        {!isSubmitted ? <motion.div key="form" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.3
      }} data-id="element-1145">
            {/* Hero Banner */}
            <div className="bg-gradient-to-br from-primary-700 to-primary px-6 py-8 text-white relative overflow-hidden" data-id="element-1146">
              <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" data-id="element-1147"></div>
              <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-accent/20 rounded-full blur-xl" data-id="element-1148"></div>

              <div className="relative z-10 flex flex-col items-center text-center" data-id="element-1149">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 shadow-lg border border-white/20" data-id="element-1150">
                  <Globe className="w-7 h-7 text-white" data-id="element-1151" />
                </div>
                <h1 className="text-2xl font-bold mb-2" data-id="element-1152">
                  Source, Import & Brand
                </h1>
                <p className="text-primary-100 text-sm leading-relaxed max-w-[280px]" data-id="element-1153">
                  Tell us what you need and we'll handle the rest — from
                  sourcing to branding to delivery.
                </p>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4 -mt-4 relative z-20" data-id="element-1154">
              {/* Personal Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50" data-id="element-1155">
                <div className="flex items-center gap-2 mb-4" data-id="element-1156">
                  <User className="w-5 h-5 text-primary" data-id="element-1157" />
                  <h2 className="text-sm font-bold text-gray-900" data-id="element-1158">
                    Personal Information
                  </h2>
                </div>
                <div className="space-y-3" data-id="element-1159">
                  <Input label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="John Doe" required data-id="element-1160" />
                  <Input label="Email Address" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required data-id="element-1161" />
                  <Input label="Phone Number" type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+234 800 000 0000" required data-id="element-1162" />
                  <Input label="Location / Country" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Lagos, Nigeria" icon={<MapPin className="w-4 h-4" data-id="element-1164" />} required data-id="element-1163" />
                </div>
              </div>

              {/* Product Details Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50" data-id="element-1165">
                <div className="flex items-center gap-2 mb-4" data-id="element-1166">
                  <Package className="w-5 h-5 text-primary" data-id="element-1167" />
                  <h2 className="text-sm font-bold text-gray-900" data-id="element-1168">
                    Product Details
                  </h2>
                </div>
                <div className="space-y-4" data-id="element-1169">
                  <div className="flex flex-col gap-1.5" data-id="element-1170">
                    <label className="text-sm font-medium text-gray-700" data-id="element-1171">
                      Product Name / Description
                    </label>
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Describe the product you want us to source in detail..." className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none" rows={4} required data-id="element-1172" />
                  </div>

                  <div className="flex flex-col gap-1.5" data-id="element-1173">
                    <label className="text-sm font-medium text-gray-700" data-id="element-1174">
                      Product Category
                    </label>
                    <div className="relative" data-id="element-1175">
                      <select name="category" value={formData.category} onChange={handleChange} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary appearance-none" required data-id="element-1176">
                        <option value="" disabled data-id="element-1177">
                          Select a category
                        </option>
                        <option value="electronics" data-id="element-1178">Electronics</option>
                        <option value="fashion" data-id="element-1179">Fashion</option>
                        <option value="beauty" data-id="element-1180">Beauty</option>
                        <option value="home" data-id="element-1181">Home & Living</option>
                        <option value="food" data-id="element-1182">Food & Beverages</option>
                        <option value="health" data-id="element-1183">Health</option>
                        <option value="sports" data-id="element-1184">Sports</option>
                        <option value="other" data-id="element-1185">Other</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" data-id="element-1186">
                        ▼
                      </div>
                    </div>
                  </div>

                  <Input label="Estimated Quantity" type="number" name="quantity" value={formData.quantity} onChange={handleChange} placeholder="e.g. 500 units" required data-id="element-1187" />

                  <div className="flex flex-col gap-1.5" data-id="element-1188">
                    <label className="text-sm font-medium text-gray-700" data-id="element-1189">
                      Budget Range
                    </label>
                    <div className="relative" data-id="element-1190">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1191" />
                      <select name="budget" value={formData.budget} onChange={handleChange} className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-3 text-sm text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary appearance-none" required data-id="element-1192">
                        <option value="" disabled data-id="element-1193">
                          Select your budget
                        </option>
                        <option value="under_100k" data-id="element-1194">Under ₦100,000</option>
                        <option value="100k_500k" data-id="element-1195">₦100,000 - ₦500,000</option>
                        <option value="500k_2m" data-id="element-1196">₦500,000 - ₦2,000,000</option>
                        <option value="2m_5m" data-id="element-1197">₦2,000,000 - ₦5,000,000</option>
                        <option value="above_5m" data-id="element-1198">Above ₦5,000,000</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" data-id="element-1199">
                        ▼
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sample Upload Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50" data-id="element-1200">
                <div className="flex items-center gap-2 mb-4" data-id="element-1201">
                  <ImageIcon className="w-5 h-5 text-primary" data-id="element-1202" />
                  <h2 className="text-sm font-bold text-gray-900" data-id="element-1203">
                    Sample Image (Optional)
                  </h2>
                </div>

                <div onClick={handleSimulateUpload} className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-2 ${hasUploaded ? 'border-success bg-success/5' : isUploading ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary hover:bg-gray-50'}`} data-id="element-1204">
                  {hasUploaded ? <>
                      <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-1" data-id="element-1205">
                        <CheckCircle2 className="w-6 h-6 text-success" data-id="element-1206" />
                      </div>
                      <p className="text-sm font-bold text-gray-900" data-id="element-1207">
                        File selected: sample_product.jpg
                      </p>
                      <p className="text-xs text-success font-medium" data-id="element-1208">
                        Upload complete
                      </p>
                    </> : isUploading ? <>
                      <div className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin mb-1" data-id="element-1209"></div>
                      <p className="text-sm font-bold text-gray-900" data-id="element-1210">
                        Uploading...
                      </p>
                    </> : <>
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-1 text-gray-500" data-id="element-1211">
                        <Upload className="w-6 h-6" data-id="element-1212" />
                      </div>
                      <p className="text-sm font-bold text-gray-900" data-id="element-1213">
                        Upload Sample Image
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1214">
                        PNG, JPG up to 5MB
                      </p>
                    </>}
                </div>
              </div>

              {/* Additional Notes Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50" data-id="element-1215">
                <div className="flex items-center gap-2 mb-4" data-id="element-1216">
                  <FileText className="w-5 h-5 text-primary" data-id="element-1217" />
                  <h2 className="text-sm font-bold text-gray-900" data-id="element-1218">
                    Additional Notes (Optional)
                  </h2>
                </div>
                <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Any specific branding requirements, materials, or target delivery dates?" className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none" rows={4} data-id="element-1219" />
              </div>

              <div className="pt-2" data-id="element-1220">
                <Button type="submit" fullWidth size="lg" className="shadow-lg shadow-primary/20" data-id="element-1221">
                  Submit Sourcing Request
                </Button>
              </div>
            </form>
          </motion.div> : <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center p-8 mt-12 text-center" data-id="element-1222">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          delay: 0.2,
          stiffness: 200,
          damping: 15
        }} className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mb-6" data-id="element-1223">
              <CheckCircle2 className="w-12 h-12 text-success" data-id="element-1224" />
            </motion.div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1225">
              Request Submitted!
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed max-w-[280px]" data-id="element-1226">
              Thank you for your request. Our sourcing team will review your
              details and get back to you within 24-48 hours.
            </p>

            <Button onClick={() => navigate(Screen.HOME)} variant="outline" className="min-w-[200px]" data-id="element-1227">
              Back to Home
            </Button>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}